import {
  Conversation,
  ConversationCreate,
  ConversationUpdate,
  ChatMessage,
  ChatMessageCreate,
} from "../types/api";
import { WORKER_API_URL, FASTAPI_APP_BASE_URL } from "./config"; // Use WORKER_API_URL with fallback to FASTAPI
import { getStoredToken } from "../stores/authStore";

const API_V1_STR = "/api/v1"; // FastAPI specific prefix
const BASE_URL = `${WORKER_API_URL}${API_V1_STR}`;

const getAuthToken = (): string | null => {
  try {
    // Prefer Zustand auth store (source of truth)
    const zToken = getStoredToken();
    if (zToken) return zToken;
  } catch {}
  try {
    if (typeof window !== "undefined") {
      return localStorage.getItem("token");
    }
  } catch {}
  return null;
};

const getHeaders = (includeAuth = true): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
};

// Robustly extract error message from various backend formats
const extractErrorMessage = async (response: Response): Promise<string> => {
  try {
    const text = await response.text();
    // Try parse JSON
    try {
      const data = JSON.parse(text);
      const detail = (data && (data.detail ?? data.error ?? data.message)) as any;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail.length) {
        // FastAPI may return list of validation errors
        const first = detail[0];
        if (first && (first.msg || first.message)) return first.msg || first.message;
      }
      if (detail && typeof detail === "object") {
        // Flatten common shapes
        if (detail.msg) return String(detail.msg);
        if (detail.message) return String(detail.message);
        return JSON.stringify(detail);
      }
      // Fall back to whole parsed data
      return typeof data === "string" ? data : JSON.stringify(data);
    } catch {
      // Not JSON
      return text || response.statusText;
    }
  } catch {
    return response.statusText || "Request failed";
  }
};

export const conversationService = {
  async createConversation(
    conversationData: ConversationCreate
  ): Promise<Conversation> {
    const primaryUrl = `${BASE_URL}/conversations/`;
    const init: RequestInit = {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(conversationData),
    };
    const response = await fetch(primaryUrl, init);
    if (response.ok) return response.json();

    // Fallback directly to FastAPI if Worker route is missing or failing
    const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/`;
    const fbRes = await fetch(fallbackUrl, init);
    if (fbRes.ok) return fbRes.json();

    const primaryMsg = await extractErrorMessage(response);
    const fallbackMsg = await extractErrorMessage(fbRes);
    throw new Error(fallbackMsg || primaryMsg || 'Failed to create conversation');
  },

  async getConversationById(conversationRef: string | number): Promise<Conversation> {
    const response = await fetch(`${BASE_URL}/conversations/${conversationRef}`, {
      headers: getHeaders(),
    });
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Conversation not found");
    }
    return response.json();
  },

  async getConversationBundle(
    conversationRef: string | number,
    limit: number = 30,
    beforeId?: number
  ): Promise<{ conversation: Conversation; messages: ChatMessage[]; paging: { hasMore: boolean; nextCursor: { before_id?: number } | null } }>
  {
    const params = new URLSearchParams({ include: 'messages', limit: String(limit) });
    if (beforeId) params.set('before_id', String(beforeId));

    const headers = getHeaders();
    const primaryUrl = `${BASE_URL}/conversations/${conversationRef}?${params.toString()}`;
    const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/${conversationRef}?${params.toString()}`;

    const coerceToBundle = (data: any): { conversation?: Conversation; messages?: ChatMessage[]; paging?: any } => {
      if (!data) return {};
      // Already in bundle shape
      if (data.conversation && Array.isArray(data.messages)) {
        // Ensure paging exists
        const hasMore = Array.isArray(data.messages) && data.messages.length === limit;
        const nextCursor = hasMore && data.messages.length > 0
          ? { before_id: Number(data.messages[0]?.message_id) }
          : null;
        return { conversation: data.conversation, messages: data.messages, paging: data.paging ?? { hasMore, nextCursor } };
      }
      // Single conversation object shape
      if (data.conversation_id || data.public_id) {
        // Some servers may include messages array directly on conversation (often empty unless include used)
        const convMessages = Array.isArray(data.messages) ? data.messages : [];
        const hasMore = convMessages.length === limit;
        const nextCursor = hasMore && convMessages.length > 0
          ? { before_id: Number(convMessages[0]?.message_id) }
          : null;
        return { conversation: data as Conversation, messages: convMessages as ChatMessage[], paging: { hasMore, nextCursor } };
      }
      return {};
    };

    // Try primary
    let primaryRes: Response | null = null;
    try {
      primaryRes = await fetch(primaryUrl, { headers });
      if (primaryRes.ok) {
        const primaryData = await primaryRes.json();
        const coerced = coerceToBundle(primaryData);
        if (coerced.conversation) {
          // Return the conversation even if messages are empty; caller can fetch messages separately
          const msgs = Array.isArray(coerced.messages) ? coerced.messages : [];
          const hasMore = msgs.length === limit;
          const nextCursor = hasMore && msgs.length > 0 ? { before_id: Number(msgs[0]?.message_id) } : null;
          return { conversation: coerced.conversation!, messages: msgs, paging: coerced.paging ?? { hasMore, nextCursor } };
        }
      }
    } catch (e) {
      // ignore; will try fallback if primary isn't ok
    }

    // Fallback to FastAPI direct only if primary was not OK
    const fallbackRes = await fetch(fallbackUrl, { headers });
    if (fallbackRes.ok) {
      const fbData = await fallbackRes.json();
      const coerced = coerceToBundle(fbData);
      if (coerced.conversation) {
        const msgs = Array.isArray(coerced.messages) ? coerced.messages : [];
        const hasMore = msgs.length === limit;
        const nextCursor = hasMore && msgs.length > 0 ? { before_id: Number(msgs[0]?.message_id) } : null;
        return { conversation: coerced.conversation!, messages: msgs, paging: coerced.paging ?? { hasMore, nextCursor } };
      }
    }
    const primaryMsg = primaryRes ? await extractErrorMessage(primaryRes) : undefined;
    const fallbackMsg = await extractErrorMessage(fallbackRes);
    throw new Error(fallbackMsg || primaryMsg || 'Failed to retrieve conversation bundle');
  },

  async getConversationsByUser(
    userId: number,
    skip: number = 0,
    limit: number = 100
  ): Promise<Conversation[]> {
    const primaryUrl = `${BASE_URL}/conversations/user/${userId}?skip=${skip}&limit=${limit}`;
    const headers = getHeaders();
    const response = await fetch(primaryUrl, { headers });
    if (response.ok) return response.json();

    // Fallback to direct FastAPI backend if Worker fails (e.g., auth middleware throttling)
    const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/user/${userId}?skip=${skip}&limit=${limit}`;
    try {
      const fallbackRes = await fetch(fallbackUrl, { headers });
      if (fallbackRes.ok) return fallbackRes.json();
      const message = await extractErrorMessage(fallbackRes);
      throw new Error(message || 'Failed to retrieve user conversations');
    } catch (fallbackErr) {
      const primaryMsg = await extractErrorMessage(response);
      const message = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
      throw new Error(message || primaryMsg || 'Failed to retrieve user conversations');
    }
  },

  async updateConversation(
    conversationRef: string | number,
    conversationData: ConversationUpdate
  ): Promise<Conversation> {
    const response = await fetch(
      `${BASE_URL}/conversations/${conversationRef}`,
      {
        method: "PUT",
        headers: getHeaders(), // Needs auth
        body: JSON.stringify(conversationData),
      }
    );
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to update conversation");
    }
    return response.json();
  },

  async generateConversationTitle(
    conversationRef: string | number
  ): Promise<Conversation | null> {
    try {
      const headers = getHeaders();
      
      // Add AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      try {
        const primaryUrl = `${BASE_URL}/conversations/${conversationRef}/generate-title`;
        const primaryRes = await fetch(primaryUrl, { 
          method: "POST", 
          headers,
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        
        if (primaryRes.ok) return primaryRes.json();
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.log('Title generation timed out after 5 seconds, trying fallback');
        }
      }

      // Fallback to direct FastAPI backend if Worker fails
      const fallbackController = new AbortController();
      const fallbackTimeoutId = setTimeout(() => fallbackController.abort(), 6000); // 6 second timeout for fallback
      
      try {
        const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/${conversationRef}/generate-title`;
        const fbRes = await fetch(fallbackUrl, { 
          method: "POST", 
          headers,
          signal: fallbackController.signal 
        });
        clearTimeout(fallbackTimeoutId);
        
        if (fbRes.ok) return fbRes.json();
      } catch (err) {
        clearTimeout(fallbackTimeoutId);
        if (err.name === 'AbortError') {
          console.log('Fallback title generation also timed out');
        }
      }

      // Don't throw - return null to indicate failure and use client-side fallback
      console.warn('Both title generation attempts failed or timed out');
      return null;
    } catch (error) {
      console.warn('Error generating title:', error);
      return null;
    }
  },

  async deleteConversation(conversationRef: string | number): Promise<Conversation> {
    const response = await fetch(
      `${BASE_URL}/conversations/${conversationRef}`,
      {
        method: "DELETE",
        headers: getHeaders(), // Needs auth
      }
    );
    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || "Failed to delete conversation");
    }
    return response.json();
  },

  // ChatMessage specific methods nested under conversation
  async createChatMessage(
    conversationRef: string | number,
    messageData: ChatMessageCreate
  ): Promise<ChatMessage> {
    const init: RequestInit = {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(messageData),
    };
    const primaryUrl = `${BASE_URL}/conversations/${conversationRef}/messages/`;
    const response = await fetch(primaryUrl, init);
    if (response.ok) return response.json();

    // Fallback to FastAPI
    const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/${conversationRef}/messages/`;
    const fbRes = await fetch(fallbackUrl, init);
    if (fbRes.ok) return fbRes.json();

    const primaryMsg = await extractErrorMessage(response);
    const fallbackMsg = await extractErrorMessage(fbRes);
    throw new Error(fallbackMsg || primaryMsg || 'Failed to send message');
  },

  async getChatMessages(
    conversationRef: string | number,
    skip: number = 0,
    limit: number = 100,
    beforeId?: number
  ): Promise<ChatMessage[]> {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (beforeId) params.set('before_id', String(beforeId));
    const headers = getHeaders();
    const primaryUrl = `${BASE_URL}/conversations/${conversationRef}/messages/?${params.toString()}`;
    const primaryRes = await fetch(primaryUrl, { headers });
    if (primaryRes.ok) return primaryRes.json();
    // Fallback to FastAPI
    const fallbackUrl = `${FASTAPI_APP_BASE_URL}/api/v1/conversations/${conversationRef}/messages/?${params.toString()}`;
    const fbRes = await fetch(fallbackUrl, { headers });
    if (fbRes.ok) return fbRes.json();
    const message = await extractErrorMessage(fbRes);
    const primaryMsg = await extractErrorMessage(primaryRes);
    throw new Error(message || primaryMsg || 'Failed to retrieve messages');
  },
};
